import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';
import Table from 'react-bootstrap/Table';

let _uid = 1;
const uid = () => _uid++;

const initialCrew = [
  { id: uid(), role: 'Lead Climber', wage: '35', hours: '8' },
  { id: uid(), role: 'Groundsman', wage: '20', hours: '8' },
];

const initialEquipment = [
  { id: uid(), name: 'Chipper', costPerHr: '15', fuelGalPerHr: '2', hoursUsed: '4' },
  { id: uid(), name: 'Truck', costPerHr: '10', fuelGalPerHr: '0', hoursUsed: '8' },
];

const n = (v) => { const x = parseFloat(v); return isNaN(x) ? 0 : x; };

const fmt = (v) => v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function Estimator() {
  // Global settings
  const [fuelPrice, setFuelPrice] = useState('3.80');
  const [laborBurden, setLaborBurden] = useState('1.4');

  // Overhead
  const [annualFixedCosts, setAnnualFixedCosts] = useState('');
  const [annualBillableHours, setAnnualBillableHours] = useState('');

  // Job details
  const [distanceMiles, setDistanceMiles] = useState('');
  const [travelSpeedMph, setTravelSpeedMph] = useState('45');
  const [dumpingFees, setDumpingFees] = useState('');
  const [materialsCost, setMaterialsCost] = useState('');

  // Crew
  const [crew, setCrew] = useState(initialCrew);

  // Equipment
  const [equipment, setEquipment] = useState(initialEquipment);

  // Profit
  const [profitMargin, setProfitMargin] = useState('20');

  // Disclaimer
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Crew handlers
  const addCrewMember = () =>
    setCrew((prev) => [...prev, { id: uid(), role: '', wage: '', hours: '' }]);

  const updateCrew = (id, field, value) =>
    setCrew((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));

  const removeCrew = (id) =>
    setCrew((prev) => prev.filter((m) => m.id !== id));

  // Equipment handlers
  const addEquipmentItem = () =>
    setEquipment((prev) => [...prev, { id: uid(), name: '', costPerHr: '', fuelGalPerHr: '', hoursUsed: '' }]);

  const updateEquipment = (id, field, value) =>
    setEquipment((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));

  const removeEquipment = (id) =>
    setEquipment((prev) => prev.filter((e) => e.id !== id));

  // Calculations
  const results = useMemo(() => {
    const fp = n(fuelPrice);
    const lb = n(laborBurden);
    const afc = n(annualFixedCosts);
    const abh = n(annualBillableHours);
    const dist = n(distanceMiles);
    const speed = n(travelSpeedMph) || 1;
    const dump = n(dumpingFees);
    const mats = n(materialsCost);
    const pm = n(profitMargin);

    const overheadRate = abh > 0 ? afc / abh : 0;

    const siteLabor = crew.reduce((sum, m) => sum + n(m.wage) * lb * n(m.hours), 0);
    const totalSiteManHours = crew.reduce((sum, m) => sum + n(m.hours), 0);

    const travelHours = (dist * 2) / speed;
    const totalBurdenedHourlyRate = crew.reduce((sum, m) => sum + n(m.wage) * lb, 0);
    const travelCost = travelHours * totalBurdenedHourlyRate;
    const travelManHours = travelHours * crew.length;

    const equipmentCost = equipment.reduce(
      (sum, e) => sum + (n(e.costPerHr) + n(e.fuelGalPerHr) * fp) * n(e.hoursUsed),
      0
    );

    const allocatedOverhead = (totalSiteManHours + travelManHours) * overheadRate;
    const breakEven = siteLabor + travelCost + equipmentCost + dump + mats + allocatedOverhead;
    const quotePrice = pm < 100 ? breakEven / (1 - pm / 100) : 0;

    return { overheadRate, siteLabor, travelCost, equipmentCost, dumpAndMats: dump + mats, allocatedOverhead, breakEven, quotePrice };
  }, [fuelPrice, laborBurden, annualFixedCosts, annualBillableHours, distanceMiles, travelSpeedMph, dumpingFees, materialsCost, crew, equipment, profitMargin]);

  const numStyle = disclaimerAccepted ? {} : { filter: 'blur(4px)', userSelect: 'none' };

  return (
    <Container className="py-4">
      <Helmet>
        <title>Free Tree Care Job Estimator & Bid Calculator | United Arborist</title>
        <meta name="description" content="Calculate tree care job costs instantly with our free estimator. Enter crew wages, equipment, travel, and overhead to get accurate break-even costs and suggested quote prices for tree service bids." />
        <meta name="keywords" content="tree care job estimator, tree service bid calculator, arborist job cost estimator, tree removal cost calculator, tree trimming price estimator, tree contractor bidding tool, free arborist calculator, tree service quote generator, tree work pricing tool" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.unitedarborist.com/estimator" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="United Arborist" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="Free Tree Care Job Estimator & Bid Calculator | United Arborist" />
        <meta property="og:description" content="Calculate break-even costs and suggested quote prices for tree care jobs. Factor in crew labor, equipment, travel, overhead, and profit margin." />
        <meta property="og:url" content="https://www.unitedarborist.com/estimator" />
        <meta property="og:image" content="https://www.unitedarborist.com/logo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Tree Care Job Estimator & Bid Calculator | United Arborist" />
        <meta name="twitter:description" content="Calculate break-even costs and suggested quote prices for tree care jobs. Free tool for arborists and tree service contractors." />
        <meta name="twitter:image" content="https://www.unitedarborist.com/logo.jpg" />
      </Helmet>
      <h3 className="mb-1">Tree Care Job Estimator</h3>
      <p className="text-muted mb-4">Calculate your break-even cost and suggested quote price.</p>

      <Row className="g-4">
        {/* ── Left column: inputs ── */}
        <Col xs={12} lg={8}>

          {/* Global Settings */}
          <Card className="mb-3 shadow-sm">
            <Card.Header className="fw-semibold" style={{ backgroundColor: 'var(--ua-parchment)', color: 'var(--ua-forest)' }}>Global Settings</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Label>Fuel Price ($/gal)</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={fuelPrice}
                      onChange={(e) => setFuelPrice(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Label>Labor Burden Multiplier</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="0.01"
                    value={laborBurden}
                    onChange={(e) => setLaborBurden(e.target.value)}
                  />
                  <Form.Text className="text-muted">e.g. 1.4 = 40% added for taxes/insurance</Form.Text>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Company Overhead */}
          <Card className="mb-3 shadow-sm">
            <Card.Header className="fw-semibold" style={{ backgroundColor: 'var(--ua-parchment)', color: 'var(--ua-forest)' }}>Company Overhead</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Label>Total Annual Fixed Costs</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="0"
                      value={annualFixedCosts}
                      onChange={(e) => setAnnualFixedCosts(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Label>Annual Billable Crew Hours</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="0"
                    value={annualBillableHours}
                    onChange={(e) => setAnnualBillableHours(e.target.value)}
                  />
                </Col>
              </Row>
              <div className="mt-3 p-2 bg-light rounded d-flex align-items-center gap-2">
                <span className="text-muted small">Per-Hour Overhead Rate:</span>
                <strong>{results.overheadRate > 0 ? `${fmt(results.overheadRate)}/hr` : '—'}</strong>
              </div>
            </Card.Body>
          </Card>

          {/* Job Details */}
          <Card className="mb-3 shadow-sm">
            <Card.Header className="fw-semibold" style={{ backgroundColor: 'var(--ua-parchment)', color: 'var(--ua-forest)' }}>Job Details</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Label>Distance to Site (miles)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    placeholder="0"
                    value={distanceMiles}
                    onChange={(e) => setDistanceMiles(e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Label>Avg Travel Speed (mph)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="45"
                    value={travelSpeedMph}
                    onChange={(e) => setTravelSpeedMph(e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Label>Dumping Fees</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="0"
                      value={dumpingFees}
                      onChange={(e) => setDumpingFees(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Label>Materials Cost</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="0"
                      value={materialsCost}
                      onChange={(e) => setMaterialsCost(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Labor / Crew */}
          <Card className="mb-3 shadow-sm">
            <Card.Header className="bg-white fw-semibold d-flex justify-content-between align-items-center">
              Labor (Crew)
              <Button variant="outline-success" size="sm" onClick={addCrewMember}>+ Add Member</Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Role</th>
                    <th>Wage ($/hr)</th>
                    <th>Hours on Site</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {crew.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="Role"
                          value={m.role}
                          onChange={(e) => updateCrew(m.id, 'role', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          placeholder="0"
                          value={m.wage}
                          onChange={(e) => updateCrew(m.id, 'wage', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          placeholder="0"
                          value={m.hours}
                          onChange={(e) => updateCrew(m.id, 'hours', e.target.value)}
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeCrew(m.id)}
                          disabled={crew.length === 1}
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Equipment */}
          <Card className="mb-3 shadow-sm">
            <Card.Header className="bg-white fw-semibold d-flex justify-content-between align-items-center">
              Equipment
              <Button variant="outline-success" size="sm" onClick={addEquipmentItem}>+ Add Equipment</Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Maint. ($/hr)</th>
                    <th>Fuel (gal/hr)</th>
                    <th>Hours Used</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {equipment.map((eq) => (
                    <tr key={eq.id}>
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="Name"
                          value={eq.name}
                          onChange={(e) => updateEquipment(eq.id, 'name', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          placeholder="0"
                          value={eq.costPerHr}
                          onChange={(e) => updateEquipment(eq.id, 'costPerHr', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          step="0.1"
                          placeholder="0"
                          value={eq.fuelGalPerHr}
                          onChange={(e) => updateEquipment(eq.id, 'fuelGalPerHr', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          placeholder="0"
                          value={eq.hoursUsed}
                          onChange={(e) => updateEquipment(eq.id, 'hoursUsed', e.target.value)}
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeEquipment(eq.id)}
                          disabled={equipment.length === 1}
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Disclaimer */}
          <Card className="mb-3 shadow-sm" style={{ borderColor: disclaimerAccepted ? 'var(--ua-emerald)' : 'var(--ua-amber)' }}>
            <Card.Header className="fw-semibold" style={{ backgroundColor: disclaimerAccepted ? 'var(--ua-emerald)' : 'var(--ua-amber)', color: disclaimerAccepted ? 'white' : 'var(--ua-forest)' }}>
              Disclaimer &amp; Acknowledgment
            </Card.Header>
            <Card.Body>
              <p className="small text-muted mb-3">
                The estimates provided by this tool are for <strong>informational and planning purposes only</strong>.
                Results are based solely on the values you enter and do not account for all variables that may affect
                actual job costs, including but not limited to site conditions, regulatory requirements, unforeseen
                complications, market fluctuations, or other factors. <strong>United Arborist makes no representations
                or warranties, express or implied, regarding the accuracy, completeness, or fitness for any particular
                purpose of these estimates.</strong> United Arborist shall not be held liable for any financial loss,
                damages, or adverse outcomes arising from the use of or reliance on this tool. You are solely
                responsible for independently verifying all estimates and for any business or financial decisions made
                based on this calculator.
              </p>
              <Form.Check
                type="checkbox"
                id="disclaimer-checkbox"
                label="I have read and understand the disclaimer. I acknowledge that United Arborist is not liable for the results or my use of this estimator."
                checked={disclaimerAccepted}
                onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                className="fw-semibold"
              />
            </Card.Body>
          </Card>

        </Col>

        {/* ── Right column: sticky results ── */}
        <Col xs={12} lg={4}>
          <div style={{ position: 'sticky', top: '76px' }}>
            <Card className="shadow" style={{ borderColor: disclaimerAccepted ? 'var(--ua-emerald)' : 'var(--ua-slate-soft)' }}
              style={!disclaimerAccepted ? { pointerEvents: 'none', userSelect: 'none' } : {}}
            >
              <Card.Header className="fw-semibold text-white" style={{ backgroundColor: disclaimerAccepted ? 'var(--ua-emerald)' : 'var(--ua-slate-mid)' }}>
                {disclaimerAccepted ? 'Estimate Summary' : 'Estimate Summary — Accept Disclaimer to Unlock'}
              </Card.Header>
              <Card.Body>
                <Table borderless size="sm" className="mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted">Labor (Site)</td>
                      <td className="text-end fw-semibold" style={numStyle}>{fmt(results.siteLabor)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Travel (Labor)</td>
                      <td className="text-end fw-semibold" style={numStyle}>{fmt(results.travelCost)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Equipment</td>
                      <td className="text-end fw-semibold" style={numStyle}>{fmt(results.equipmentCost)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Materials &amp; Dump</td>
                      <td className="text-end fw-semibold" style={numStyle}>{fmt(results.dumpAndMats)}</td>
                    </tr>
                    <tr className="table-warning">
                      <td>Allocated Overhead</td>
                      <td className="text-end fw-semibold" style={numStyle}>{fmt(results.allocatedOverhead)}</td>
                    </tr>
                    <tr className="border-top">
                      <td className="fw-bold pt-2">Break-Even Cost</td>
                      <td className="text-end fw-bold fs-6 pt-2" style={numStyle}>{fmt(results.breakEven)}</td>
                    </tr>
                  </tbody>
                </Table>

                <hr />

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Desired Profit Margin</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="0"
                      max="99"
                      value={profitMargin}
                      onChange={(e) => setProfitMargin(e.target.value)}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>

                <div className="p-3 rounded text-center" style={{ backgroundColor: 'var(--ua-emerald-soft)' }}>
                  <div className="small mb-1" style={{ color: 'var(--ua-slate-mid)' }}>Suggested Quote Price</div>
                  <div className="fw-bold fs-2" style={disclaimerAccepted ? { color: 'var(--ua-emerald)' } : { color: 'var(--ua-emerald)', filter: 'blur(10px)', userSelect: 'none' }}>{fmt(results.quotePrice)}</div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default Estimator;
